// src/firebase/services.js
// All Firestore + Storage operations in one place

import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc,
  deleteDoc, setDoc, query, orderBy, serverTimestamp
} from "firebase/firestore";
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject
} from "firebase/storage";
import {
  createUserWithEmailAndPassword, updateProfile
} from "firebase/auth";
import { db, storage, auth } from "./config";

// ── USERS ────────────────────────────────────────────────────────────────────

/**
 * Create a new user in Firebase Auth + store their role in Firestore.
 * Only admins should call this from the Admin Panel.
 */
export const createUser = async ({ name, email, password, role }) => {
  // Create auth account
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName: name });

  // Store role + metadata in Firestore users collection
  await setDoc(doc(db, "users", cred.user.uid), {
    name,
    email,
    role,          // "admin" | "hr" | "principal" | "viewer"
    createdAt: serverTimestamp(),
    active: true
  });

  return cred.user;
};

/** Fetch all users from Firestore (for admin panel) */
export const getUsers = async () => {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/** Update a user's role or active status */
export const updateUser = async (uid, data) => {
  await updateDoc(doc(db, "users", uid), data);
};

/** Fetch single user profile by UID */
export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// ── EMPLOYEES ────────────────────────────────────────────────────────────────

/** Add a new employee document */
export const addEmployee = async (data) => {
  const docRef = await addDoc(collection(db, "employees"), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
};

/** Get all employees, ordered by name */
export const getEmployees = async () => {
  const q = query(collection(db, "employees"), orderBy("name"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/** Update employee document */
export const updateEmployee = async (id, data) => {
  await updateDoc(doc(db, "employees", id), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

/** Delete employee document */
export const deleteEmployee = async (id) => {
  await deleteDoc(doc(db, "employees", id));
};

// ── ATTENDANCE ────────────────────────────────────────────────────────────────

/**
 * Save attendance for a specific date.
 * Document ID = the date string (e.g. "2025-04-05")
 */
export const saveAttendance = async (date, records) => {
  await setDoc(doc(db, "attendance", date), {
    date,
    records,
    savedAt: serverTimestamp()
  });
};

/** Get attendance for a single date */
export const getAttendanceByDate = async (date) => {
  const snap = await getDoc(doc(db, "attendance", date));
  return snap.exists() ? snap.data() : null;
};

/** Get all attendance records */
export const getAllAttendance = async () => {
  const q = query(collection(db, "attendance"), orderBy("date", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data());
};

// ── FILE STORAGE ─────────────────────────────────────────────────────────────

/**
 * Upload a file to Firebase Storage.
 * Returns the public download URL.
 * onProgress(0–100) is called with upload progress.
 *
 * Path structure:
 *   employees/{employeeId}/photo/{filename}
 *   employees/{employeeId}/documents/{filename}
 */
export const uploadFile = (file, path, onProgress) => {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snapshot) => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress && onProgress(pct);
      },
      (error) => reject(error),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      }
    );
  });
};

/** Delete a file from Storage by its full path */
export const deleteFile = async (path) => {
  try {
    await deleteObject(ref(storage, path));
  } catch (e) {
    // File may already be deleted – ignore
  }
};

/** Helper: Upload employee photo, return URL */
export const uploadEmployeePhoto = async (employeeId, file, onProgress) => {
  const ext = file.name.split(".").pop();
  const path = `employees/${employeeId}/photo/photo.${ext}`;
  return uploadFile(file, path, onProgress);
};

/** Helper: Upload an attachment for an employee, return { name, url, size, type, path } */
export const uploadAttachment = async (employeeId, file, onProgress) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `employees/${employeeId}/documents/${Date.now()}_${safeName}`;
  const url = await uploadFile(file, path, onProgress);
  return { name: file.name, url, size: file.size, type: file.type, path };
};
